import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import {
  DoorOpen, Plus, Minus, Check, Layers, Bed, IndianRupee,
  Sparkles, Loader2, ChevronRight, Copy
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROOM_TYPES = [
  { id: 'Single',     label: '1-Sharing',  beds: 1 },
  { id: 'Double',     label: '2-Sharing',  beds: 2 },
  { id: 'Triple',     label: '3-Sharing',  beds: 3 },
  { id: 'Dormitory',  label: '4+ Dorm',    beds: 4 },
];

const FLOORS = ['G', '1', '2', '3', '4', '5'];

const RENT_PRESETS = [5000, 8000, 10000, 12000, 15000, 20000];

const AMENITIES = [
  { id: 'AC',                 label: 'AC',         icon: '❄️' },
  { id: 'WiFi',               label: 'WiFi',       icon: '📶' },
  { id: 'Attached Bathroom',  label: 'Bathroom',   icon: '🚿' },
  { id: 'TV',                 label: 'TV',         icon: '📺' },
  { id: 'Fridge',             label: 'Fridge',     icon: '🧊' },
  { id: 'Balcony',            label: 'Balcony',    icon: '🏞️' },
  { id: 'Geyser',             label: 'Geyser',     icon: '🔥' },
  { id: 'Wardrobe',           label: 'Almirah',    icon: '🚪' },
  { id: 'Table',              label: 'Table',      icon: '🪑' },
  { id: 'Chair',              label: 'Chair',      icon: '💺' },
  { id: 'Washroom',           label: 'Washroom',   icon: '🚽' },
  { id: 'Microwave',          label: 'Microwave',  icon: '🍱' },
  { id: 'Laundry',            label: 'Laundry',    icon: '🧺' },
  { id: 'CCTV',               label: 'CCTV',       icon: '📹' },
  { id: 'Cooler',             label: 'Cooler',     icon: '🌀' },
  { id: 'Food',               label: 'Food',       icon: '🍽️' },
];

// Room tags: Corner/Large/Furnished/Male/Female etc.
const ROOM_TAGS = [
  { group: 'Layout',      items: ['Corner Room', 'Large Room', 'Ventilation', 'Hall'] },
  { group: 'Furnishing',  items: ['Furnished', 'Semi-Furnished', 'Unfurnished'] },
  { group: 'For',         items: ['Male', 'Female', 'Co-Ed'] },
  { group: 'Bathroom',    items: ['Attached', 'Non-Attached'] },
  { group: 'Term',        items: ['Short Term', 'Long Term'] },
];

const ROOM_COUNT_PRESETS = [1, 5, 10, 20, 50, 100];

/**
 * Tap-first bulk room creator. Zero-typing defaults:
 *   - 10 rooms, Single, floor 1, 1 bed, ₹8000 rent, ₹16000 deposit, AC+WiFi, starting 101
 * Everything is a chip / stepper. Keyboard input only for fine-tuning.
 */
const BulkAddRoomsModal = ({ isOpen, onClose, onSuccess, propertyId, propertyName, token }) => {
  const [count, setCount]          = useState(10);
  const [startNumber, setStartNumber] = useState(101);
  const [floor, setFloor]          = useState('1');
  const [roomType, setRoomType]    = useState('Single');
  const [totalBeds, setTotalBeds]  = useState(1);
  const [rent, setRent]            = useState(8000);
  const [depositMultiplier, setDepositMultiplier] = useState(2); // 1× 2× 3×
  const [prefix, setPrefix]        = useState('');   // e.g. "A-" or "" (number-only)
  const [amenities, setAmenities]  = useState(['AC', 'WiFi']);
  const [tags, setTags]            = useState([]);
  const [creating, setCreating]    = useState(false);

  const deposit = useMemo(() => rent * depositMultiplier, [rent, depositMultiplier]);

  // Preview: show first 3 and last 2 room numbers
  const preview = useMemo(() => {
    const all = [];
    for (let i = 0; i < count; i++) all.push(`${prefix}${startNumber + i}`);
    if (count <= 5) return all;
    return [...all.slice(0, 3), '…', ...all.slice(-2)];
  }, [count, startNumber, prefix]);

  const toggleAmenity = (id) => {
    setAmenities(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleTag = (id) => {
    setTags(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const setRoomTypeAndBeds = (t) => {
    setRoomType(t.id);
    setTotalBeds(t.beds);
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      const res = await axios.post(
        `${API}/properties/${propertyId}/rooms/bulk`,
        {
          count, start_number: startNumber, floor, room_type: roomType,
          total_beds: totalBeds, rent_per_bed: rent, deposit_amount: deposit,
          amenities: [...amenities, ...tags], prefix,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { created_count, skipped_count, skipped_numbers } = res.data;
      toast({
        title: `🎉 ${created_count} rooms created!`,
        description: skipped_count > 0
          ? `${skipped_count} skipped (already exist): ${(skipped_numbers || []).slice(0,5).join(', ')}${skipped_count > 5 ? '…' : ''}`
          : `Added ${created_count} rooms to ${propertyName} in one tap.`,
      });
      // Only call onSuccess — parent handles closing via its own setState
      onSuccess();
    } catch (e) {
      toast({
        title: 'Failed to create rooms',
        description: e.response?.data?.detail || 'Please try again',
        variant: 'destructive',
      });
    } finally { setCreating(false); }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl p-0 overflow-hidden gap-0" data-testid="bulk-add-rooms-modal">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white p-5">
          <div className="flex items-center gap-2 mb-1">
            <Layers size={20} />
            <DialogTitle className="text-lg font-semibold">Bulk Add Rooms</DialogTitle>
          </div>
          <DialogDescription className="text-white/80 text-xs">
            Create dozens of rooms in seconds — tap presets, no typing needed.
            <span className="ml-1">Adding to <strong className="text-white">{propertyName}</strong></span>
          </DialogDescription>
        </div>

        <div className="max-h-[70vh] overflow-y-auto px-5 py-4 space-y-5">
          {/* 1. HOW MANY ROOMS */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider">How many rooms?</label>
              <div className="flex items-center gap-2">
                <button
                  data-testid="count-minus"
                  onClick={() => setCount(Math.max(1, count - 1))}
                  className="w-7 h-7 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
                ><Minus size={14} /></button>
                <span data-testid="count-value" className="text-2xl font-bold text-blue-600 w-12 text-center">{count}</span>
                <button
                  data-testid="count-plus"
                  onClick={() => setCount(Math.min(200, count + 1))}
                  className="w-7 h-7 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
                ><Plus size={14} /></button>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {ROOM_COUNT_PRESETS.map(n => (
                <button
                  key={n}
                  data-testid={`count-preset-${n}`}
                  onClick={() => setCount(n)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${count === n
                    ? 'bg-blue-600 text-white shadow-md scale-105'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  {n}
                </button>
              ))}
            </div>
          </section>

          {/* 2. START FROM / FLOOR */}
          <section className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2 block">Start number</label>
              <div className="flex items-center gap-2">
                <button onClick={() => setStartNumber(Math.max(1, startNumber - 1))} className="w-7 h-7 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center">
                  <Minus size={14} />
                </button>
                <input
                  data-testid="start-number-input"
                  type="number"
                  value={startNumber}
                  onChange={(e) => setStartNumber(parseInt(e.target.value) || 1)}
                  className="w-20 text-center text-lg font-bold border border-gray-300 rounded-lg py-1.5"
                />
                <button onClick={() => setStartNumber(startNumber + 1)} className="w-7 h-7 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center">
                  <Plus size={14} />
                </button>
                <div className="flex gap-1 ml-1">
                  {[101, 201, 301].map(n => (
                    <button
                      key={n}
                      data-testid={`start-preset-${n}`}
                      onClick={() => setStartNumber(n)}
                      className={`text-xs px-2 py-1 rounded-full ${startNumber === n ? 'bg-blue-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                    >{n}</button>
                  ))}
                </div>
              </div>
              <p className="text-[11px] text-gray-500 mt-1.5">Tap letters to add a prefix (optional)</p>
              <div className="flex gap-1 mt-1">
                {['', 'A-', 'B-', 'G-'].map(p => (
                  <button
                    key={p || 'none'}
                    data-testid={`prefix-${p || 'none'}`}
                    onClick={() => setPrefix(p)}
                    className={`text-xs px-2 py-1 rounded-full ${prefix === p ? 'bg-purple-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                  >{p || 'None'}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2 block">Floor</label>
              <div className="flex flex-wrap gap-1.5">
                {FLOORS.map(f => (
                  <button
                    key={f}
                    data-testid={`floor-${f}`}
                    onClick={() => setFloor(f)}
                    className={`w-11 h-11 rounded-xl text-sm font-bold transition-all ${floor === f
                      ? 'bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-md scale-105'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                  >{f === 'G' ? 'G' : f}</button>
                ))}
              </div>
            </div>
          </section>

          {/* 3. ROOM TYPE */}
          <section>
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2 block">Sharing type</label>
            <div className="grid grid-cols-4 gap-2">
              {ROOM_TYPES.map(t => (
                <button
                  key={t.id}
                  data-testid={`room-type-${t.id}`}
                  onClick={() => setRoomTypeAndBeds(t)}
                  className={`p-3 rounded-xl border-2 transition-all ${roomType === t.id
                    ? 'border-blue-500 bg-blue-50 shadow-sm scale-[1.02]'
                    : 'border-gray-200 hover:border-blue-300 bg-white'}`}
                >
                  <Bed size={18} className={`mx-auto mb-1 ${roomType === t.id ? 'text-blue-600' : 'text-gray-400'}`} />
                  <p className="text-xs font-semibold text-gray-900">{t.label}</p>
                  <p className="text-[10px] text-gray-500">{t.beds} bed{t.beds > 1 ? 's' : ''}</p>
                </button>
              ))}
            </div>
          </section>

          {/* 4. RENT (steppers + presets) */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Rent per bed</label>
              <div className="flex items-center gap-2">
                <button onClick={() => setRent(Math.max(500, rent - 500))} className="w-7 h-7 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center">
                  <Minus size={14} />
                </button>
                <span data-testid="rent-value" className="text-xl font-bold text-green-700 min-w-[90px] text-center">₹{rent.toLocaleString()}</span>
                <button onClick={() => setRent(rent + 500)} className="w-7 h-7 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center">
                  <Plus size={14} />
                </button>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {RENT_PRESETS.map(r => (
                <button
                  key={r}
                  data-testid={`rent-preset-${r}`}
                  onClick={() => setRent(r)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${rent === r
                    ? 'bg-green-600 text-white shadow-md scale-105'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  ₹{r.toLocaleString()}
                </button>
              ))}
            </div>
          </section>

          {/* 5. DEPOSIT (auto-calculated) */}
          <section>
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2 block">
              Deposit <span className="text-gray-400 font-normal normal-case">(auto from rent)</span>
            </label>
            <div className="flex flex-wrap items-center gap-2">
              {[1, 2, 3].map(m => (
                <button
                  key={m}
                  data-testid={`deposit-x${m}`}
                  onClick={() => setDepositMultiplier(m)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${depositMultiplier === m
                    ? 'bg-orange-600 text-white shadow-md scale-105'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  {m}× rent
                </button>
              ))}
              <span className="ml-2 text-sm text-gray-600">=</span>
              <span className="text-lg font-bold text-orange-700" data-testid="deposit-value">₹{deposit.toLocaleString()}</span>
            </div>
          </section>

          {/* 6. AMENITIES */}
          <section>
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2 block">
              Amenities <span className="text-gray-400 font-normal normal-case">({amenities.length} selected)</span>
            </label>
            <div className="flex flex-wrap gap-1.5">
              {AMENITIES.map(a => (
                <button
                  key={a.id}
                  data-testid={`amenity-${a.id}`}
                  onClick={() => toggleAmenity(a.id)}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${amenities.includes(a.id)
                    ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'}`}
                >
                  <span>{a.icon}</span>
                  <span>{a.label}</span>
                  {amenities.includes(a.id) && <Check size={12} />}
                </button>
              ))}
            </div>
          </section>

          {/* 7. ROOM TAGS (grouped pills) */}
          <section>
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2 block">
              Room Tags <span className="text-gray-400 font-normal normal-case">({tags.length} selected — optional)</span>
            </label>
            <div className="space-y-2">
              {ROOM_TAGS.map(group => (
                <div key={group.group} className="flex flex-wrap items-center gap-1.5">
                  <span className="text-[11px] text-gray-500 font-medium w-20 shrink-0">{group.group}:</span>
                  {group.items.map(t => (
                    <button
                      key={t}
                      data-testid={`tag-${t.replace(/\s+/g, '-')}`}
                      onClick={() => toggleTag(t)}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-all ${tags.includes(t)
                        ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-400'}`}
                    >{t}</button>
                  ))}
                </div>
              ))}
            </div>
          </section>

          {/* PREVIEW */}
          <section className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-xl p-4" data-testid="bulk-preview">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={14} className="text-purple-600" />
              <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Preview</p>
            </div>
            <p className="text-sm text-gray-800">
              Will create <strong className="text-blue-700">{count} room{count > 1 ? 's' : ''}</strong> on Floor {floor}:
              <span className="ml-2 font-mono text-purple-700 text-xs bg-white px-2 py-0.5 rounded">
                {preview.map((r, i) => <span key={r}>{r}{i < preview.length - 1 ? ', ' : ''}</span>)}
              </span>
            </p>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-2 text-xs text-gray-600">
              <span>{roomType} ({totalBeds} bed{totalBeds > 1 ? 's' : ''}/room)</span>
              <span>·</span>
              <span className="text-green-700 font-semibold">₹{rent.toLocaleString()}/bed</span>
              <span>·</span>
              <span className="text-orange-700 font-semibold">₹{deposit.toLocaleString()} deposit</span>
              <span>·</span>
              <span>{amenities.length} amenities</span>
              {tags.length > 0 && <><span>·</span><span>{tags.length} tags</span></>}
            </div>
            <div className="flex items-center gap-2 mt-2 pt-2 border-t border-blue-200/60">
              <span className="text-xs text-gray-600">Monthly revenue potential:</span>
              <span className="text-base font-bold text-green-700">
                ₹{(rent * totalBeds * count).toLocaleString()}
              </span>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="border-t bg-gray-50 px-5 py-3 flex items-center justify-between">
          <Button variant="outline" onClick={onClose} disabled={creating} data-testid="bulk-cancel-btn">
            Cancel
          </Button>
          <Button
            data-testid="bulk-create-btn"
            onClick={handleCreate}
            disabled={creating || count < 1}
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:opacity-90 text-white px-6"
          >
            {creating ? (
              <><Loader2 className="animate-spin mr-2" size={14} /> Creating {count} rooms…</>
            ) : (
              <><Plus size={14} className="mr-1" /> Create {count} Rooms <ChevronRight size={14} className="ml-1" /></>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BulkAddRoomsModal;
